import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  FormControlLabel,
  Checkbox,
  Paper,
} from '@mui/material';
import { Edit as EditIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { providerVehiclesAPI } from '../services/api';
import { ProviderVehicle, ProviderVehicleCreate } from '../types';

interface ProviderVehiclesSectionProps {
  providerId: number;
}

const emptyForm = (): Omit<ProviderVehicleCreate, 'provider_id'> => ({
  license_plate: '',
  make: '',
  model: '',
  year: null,
  color: '',
  seats: null,
  vin: '',
  notes: '',
  is_active: true,
});

const ProviderVehiclesSection: React.FC<ProviderVehiclesSectionProps> = ({ providerId }) => {
  const [vehicles, setVehicles] = useState<ProviderVehicle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<ProviderVehicle | null>(null);
  const [formData, setFormData] = useState(emptyForm());

  const fetchVehicles = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await providerVehiclesAPI.list({
        providerId,
        includeInactive: true,
      });
      setVehicles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError('Failed to load vehicles.');
    } finally {
      setIsLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  const openCreate = () => {
    setIsEditMode(false);
    setSelectedVehicle(null);
    setFormData(emptyForm());
    setShowErrors(false);
    setIsDialogOpen(true);
  };

  const openEdit = (vehicle: ProviderVehicle) => {
    setIsEditMode(true);
    setSelectedVehicle(vehicle);
    setFormData({
      license_plate: vehicle.license_plate,
      make: vehicle.make,
      model: vehicle.model,
      year: vehicle.year ?? null,
      color: vehicle.color || '',
      seats: vehicle.seats ?? null,
      vin: vehicle.vin || '',
      notes: vehicle.notes || '',
      is_active: vehicle.is_active,
    });
    setShowErrors(false);
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      setError(null);
      if (!formData.license_plate.trim() || !formData.make.trim() || !formData.model.trim()) {
        setShowErrors(true);
        setError('License plate, make, and model are required.');
        return;
      }
      setShowErrors(false);

      const payload: ProviderVehicleCreate = {
        provider_id: providerId,
        license_plate: formData.license_plate.trim(),
        make: formData.make.trim(),
        model: formData.model.trim(),
        year: formData.year ? Number(formData.year) : null,
        color: formData.color?.trim() || null,
        seats: formData.seats ? Number(formData.seats) : null,
        vin: formData.vin?.trim() || null,
        notes: formData.notes?.trim() || null,
        is_active: formData.is_active !== false,
      };

      if (isEditMode && selectedVehicle) {
        await providerVehiclesAPI.update(selectedVehicle.vehicle_id, payload);
      } else {
        await providerVehiclesAPI.create(payload);
      }
      setIsDialogOpen(false);
      await fetchVehicles();
    } catch (err: any) {
      console.error(err);
      const detail = err?.response?.data?.detail || err?.message;
      setError(typeof detail === 'string' ? detail : 'Failed to save vehicle.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleDelete = async (vehicle: ProviderVehicle) => {
    if (!window.confirm(`Deactivate vehicle ${vehicle.license_plate}?`)) return;
    try {
      await providerVehiclesAPI.delete(vehicle.vehicle_id);
      await fetchVehicles();
    } catch (err) {
      console.error(err);
      setError('Failed to deactivate vehicle.');
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mt: 3,
        borderRadius: 4,
        border: '1px solid',
        borderColor: '#2D4B73',
        boxShadow: '0 4px 20px rgba(45, 75, 115, 0.08)',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2D4B73' }}>
          Vehicles
        </Typography>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreate}>
          Add Vehicle
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>License Plate</TableCell>
                <TableCell>Make / Model</TableCell>
                <TableCell>Year</TableCell>
                <TableCell>Color</TableCell>
                <TableCell>Seats</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vehicles.map((vehicle) => (
                <TableRow key={vehicle.vehicle_id}>
                  <TableCell>{vehicle.license_plate}</TableCell>
                  <TableCell>
                    {vehicle.make} {vehicle.model}
                  </TableCell>
                  <TableCell>{vehicle.year || '—'}</TableCell>
                  <TableCell>{vehicle.color || '—'}</TableCell>
                  <TableCell>{vehicle.seats ?? '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={vehicle.is_active ? 'Active' : 'Inactive'}
                      color={vehicle.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => openEdit(vehicle)} size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {vehicle.is_active && (
                      <IconButton onClick={() => handleDelete(vehicle)} size="small" color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {vehicles.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No vehicles found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit Vehicle' : 'Add Vehicle'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="License Plate"
                value={formData.license_plate}
                error={showErrors && !formData.license_plate.trim()}
                helperText={showErrors && !formData.license_plate.trim() ? 'License plate is required' : undefined}
                onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Year"
                type="number"
                value={formData.year ?? ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    year: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Make"
                value={formData.make}
                error={showErrors && !formData.make.trim()}
                helperText={showErrors && !formData.make.trim() ? 'Make is required' : undefined}
                onChange={(e) => setFormData({ ...formData, make: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Model"
                value={formData.model}
                error={showErrors && !formData.model.trim()}
                helperText={showErrors && !formData.model.trim() ? 'Model is required' : undefined}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Color"
                value={formData.color || ''}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Seats"
                type="number"
                value={formData.seats ?? ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    seats: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="VIN"
                value={formData.vin || ''}
                onChange={(e) => setFormData({ ...formData, vin: e.target.value })}
                inputProps={{ maxLength: 17 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                minRows={2}
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
            {isEditMode && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_active !== false}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ProviderVehiclesSection;
